import {
  useObjectNameFromKrypton,
  hasFieldDetails
} from "@certa/queries/queries/comms";
import type {
  CommentsObjectType,
  ConversationCommsModel,
  ConversationMessageCommsModel,
  LatestMessage
} from "@certa/queries/queries/comms";
import { groupBy } from "lodash-es";
import { ChevronRight } from "@certa/icons/components/ChevronRight";
import { Badge } from "antd";
import { Messages } from "@certa/icons/components/Messages";
import { Text, Divider, getAvatarLetters } from "@certa/blocks/thanos";

import {
  AvatarColors,
  AvatarSizes,
  Avatar
} from "@certa/catalyst/components/Avatar";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Tooltip } from "@certa/catalyst/components/Tooltip";
import {
  Typography,
  TypographyVariants,
  TypographyColors
} from "@certa/catalyst/components/Typography";
import { CatalystColors } from "@certa/catalyst/constants/styles";
import { useCommentMessageFormatComms } from "../../hooks/useCommentMessageFormat";
import { css } from "emotion";
import { messageStyles } from "./CommentsRedesigned.styles";
import { useMemo } from "react";
import { HierarchyBreadcrumbsComms } from "./HierarchyBreadcrumbs";
import { format, formatDistanceToNow } from "date-fns";
import { Loader } from "@certa/blocks";
import type { ConversationThread } from "@certa/types";
import { ListWrapper } from "@certa/common/components/ListWrapper";
import type { UseInfiniteQueryResult, InfiniteData } from "react-query";
import type { MessagesCount } from "@certa/queries/queries/comments/getMessagesCount.query";

export type InfiniteConversationCommsModel = Omit<
  UseInfiniteQueryResult<ConversationCommsModel>,
  "data"
> & {
  data?: InfiniteData<ConversationCommsModel>;
  loadMore: () => Promise<void>;
};

type ConversationListItemProps = {
  threadsComms: InfiniteConversationCommsModel;
  handleChannelSelectComms: (conversationThread: ConversationThread) => void;
  stepCounts?: Record<string, MessagesCount>;
  fieldCounts?: Record<string, MessagesCount>;
};

const ConversationListItemAppRedesign = ({
  threadsComms,
  handleChannelSelectComms,
  stepCounts,
  fieldCounts
}: ConversationListItemProps) => {
  const conversations = useMemo(() => {
    if (!threadsComms?.data?.pages) {
      return [];
    }

    // Combine all pages of results
    const allResults = threadsComms.data?.pages.flatMap(page => page.results);

    const groupedThreads = groupBy(
      allResults,
      thread => `${thread.objectType}-${thread.objectId}`
    );

    return Object.entries(groupedThreads).map(([key, threads]) => {
      const messageCount = threads.reduce(
        (sum, thread) => sum + thread.messageCount,
        0
      );
      const latestMessage = threads.reduce((latest, thread) =>
        latest.latestMessage &&
        thread.latestMessage &&
        new Date(thread.latestMessage.postedAt) >
          new Date(latest.latestMessage.postedAt)
          ? thread
          : latest
      );

      return {
        key,
        threads,
        objectType: key.split("-")[0],
        objectId: key.split("-")[1],
        messageCount,
        latestMessage: latestMessage.latestMessage
      };
    });
  }, [threadsComms]);

  const handleOnClick = (conversation: LatestConversationComms) => {
    const thread = conversation.threads[0]; // Get first thread from group
    handleChannelSelectComms({
      conversationId: thread.id,
      objectId: conversation.objectId,
      objectType: conversation.objectType,
      path: {
        groupId: "",
        groupName: "",
        stepId: "",
        stepName: "",
        fieldId: "",
        fieldName: ""
      }
    });
  };

  return (
    <ListWrapper
      options={conversations.filter(
        conversation => conversation?.latestMessage?.body
      )}
      aria-label="conversations list"
      getKey={conversation => conversation.key}
      getLabel={conversation => conversation.latestMessage?.body || ""}
      onClick={handleOnClick}
      render={conversation => (
        <ConversationItemComms
          latestConversation={conversation}
          handleChannelSelectComms={handleChannelSelectComms}
          stepCounts={stepCounts}
          fieldCounts={fieldCounts}
        />
      )}
      loadMore={threadsComms.loadMore}
      hasMore={threadsComms.hasNextPage}
      itemsWithoutBorder={true}
      getScrollParent={() => document.getElementById("catalyst-drawer-content")}
    />
  );
};

type LatestConversationComms = {
  key: string;
  threads: ConversationMessageCommsModel[];
  objectType: string;
  objectId: string;
  messageCount: number;
  latestMessage: LatestMessage;
};

const ConversationItemComms = ({
  latestConversation,
  handleChannelSelectComms,
  stepCounts,
  fieldCounts
}: {
  latestConversation: LatestConversationComms;
  handleChannelSelectComms: (conversationThread: ConversationThread) => void;
  stepCounts?: Record<string, MessagesCount>;
  fieldCounts?: Record<string, MessagesCount>;
}) => {
  const { latestMessage, objectType, objectId } = latestConversation;

  const unreadCount =
    objectType === "field"
      ? fieldCounts?.[objectId].unread
      : stepCounts?.[objectId].unread;

  const {
    conversationId,
    body: messageBody,
    mentionedUsers,
    mentionedGroups,
    postedBy,
    postedAt
  } = latestMessage;
  const formattedMessage = useCommentMessageFormatComms(
    messageBody,
    mentionedUsers,
    mentionedGroups
  );
  const { data: pathDetails, isLoading: isLoadingPathData } =
    useObjectNameFromKrypton({
      objectType: objectType as CommentsObjectType,
      objectId
    });

  const conversationThread: ConversationThread = {
    conversationId,
    objectId,
    objectType,
    path: {
      groupId: pathDetails?.stepGroupId ?? "",
      groupName: pathDetails?.stepGroupName ?? "",
      stepId: pathDetails?.stepId ?? "",
      stepName: pathDetails?.stepName ?? "",
      fieldId:
        pathDetails && hasFieldDetails(pathDetails) ? pathDetails.fieldId : "",
      fieldName:
        pathDetails && hasFieldDetails(pathDetails) ? pathDetails.fieldName : ""
    }
  };

  return (
    <Stack
      direction="vertical"
      padding="16"
      tabIndex={0}
      role="button"
      onClick={() => handleChannelSelectComms(conversationThread)}
      dangerouslySetClassName={css({
        cursor: "pointer",
        border: `1px solid ${CatalystColors.NEUTRAL_400}`,
        borderRadius: "0.75rem",
        "&:hover": {
          backgroundColor: CatalystColors.TEAL_100
        }
      })}
    >
      <Stack direction="horizontal" width="100%" justify="space-between">
        {isLoadingPathData ? (
          <Loader size="extra-small" />
        ) : (
          <HierarchyBreadcrumbsComms
            taskLaneName={pathDetails?.stepGroupName ?? ""}
            taskName={pathDetails?.stepName ?? ""}
            fieldName={
              pathDetails && hasFieldDetails(pathDetails)
                ? pathDetails.fieldName
                : ""
            }
          />
        )}
        <Stack direction="horizontal" justify="end" gap="8" padding="4">
          <Badge dot count={unreadCount ?? 0} offset={[-2, 2]}>
            <Messages />
          </Badge>
          <ChevronRight color={CatalystColors.BRAND_500} />
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="vertical" gap="16" align="start">
        <Stack
          direction="horizontal"
          justify="space-between"
          width="100%"
          gap="12"
        >
          <Stack
            direction="horizontal"
            justify="start"
            gap="8"
            dangerouslySetClassName={css({
              minWidth: 0
            })}
          >
            <Avatar
              size={AvatarSizes.BIG}
              color={AvatarColors.BRAND}
              aria-label={postedBy.fullName}
            >
              {getAvatarLetters(postedBy.fullName)}
            </Avatar>
            <Stack
              direction="vertical"
              gap="4"
              dangerouslySetClassName={css({
                minWidth: 0
              })}
            >
              <Stack direction="horizontal" gap="8">
                <Typography
                  variant={TypographyVariants.LABEL_SM_BOLD}
                  color={TypographyColors.NEUTRAL_800}
                >
                  {postedBy.fullName}
                </Typography>

                <Stack
                  direction="horizontal"
                  align="start"
                  justify="end"
                  gap="8"
                  dangerouslySetClassName={css({
                    flexShrink: 0,
                    minWidth: 0
                  })}
                >
                  <Tooltip content={format(new Date(postedAt), "PPpp")}>
                    <Typography
                      variant={TypographyVariants.LABEL_SM}
                      color={TypographyColors.NEUTRAL_800}
                    >
                      {formatDistanceToNow(new Date(postedAt), {
                        addSuffix: true
                      })}
                    </Typography>
                  </Tooltip>
                </Stack>
              </Stack>

              <Text
                variant="p1-regular"
                lineClamp={3}
                className={messageStyles}
                dangerouslySetInnerHTML={{
                  __html: formattedMessage
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { ConversationListItemAppRedesign };
